import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

function Terms() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="App-main">
      <div className="report-page" style={{ maxWidth: '900px', margin: '0 auto' }}>
        <h2>Terms of Service</h2>
        <p className="status-text">Last updated: September 1, 2026 &middot; Version 1.0</p>

        <div className="dossier-section">
          <h3>1. Introduction</h3>
          <p>
            These Terms of Service ("Terms") govern your access to and use of the platform operated by Rentual Corp
            ("Rentual", "we", "us", "our") at rentual.co (the "Platform"). By creating an account, accessing, or
            using the Platform, you agree to be bound by these Terms and our{' '}
            <Link to="/privacy">Privacy Policy</Link>.
          </p>
          <p>
            If you do not agree to these Terms, do not create an account or use the Platform.
          </p>
        </div>

        <div className="dossier-section">
          <h3>2. Definitions</h3>
          <ul>
            <li><strong>"Platform"</strong> means the Rentual website at rentual.co, including all features, tools, APIs, and related services.</li>
            <li><strong>"Services"</strong> means all functionality provided through the Platform, including property listings, tenant applications, screening workflows, identity verification, AI-assisted document review, rent collection, lease management, and communications.</li>
            <li><strong>"User"</strong> means any individual or entity that accesses or uses the Platform, including Landlords and Tenants.</li>
            <li><strong>"Landlord"</strong> means a User who lists properties, manages tenancies, or uses the Platform to screen tenants.</li>
            <li><strong>"Tenant"</strong> means a User who applies for tenancies, submits documents, or uses the Platform in connection with a rental application or active tenancy.</li>
            <li><strong>"Content"</strong> means all text, images, documents, data, and other materials uploaded, submitted, or generated through the Platform.</li>
          </ul>
        </div>

        <div className="dossier-section">
          <h3>3. Account Registration</h3>
          <ul>
            <li>You must be at least the age of majority in your jurisdiction to create an account.</li>
            <li>You must provide accurate, complete, and current information during registration and keep it updated.</li>
            <li>Each individual may maintain only one account of each type (Landlord or Tenant).</li>
            <li>You are solely responsible for maintaining the confidentiality of your password and for all activity that occurs under your account.</li>
            <li>You must notify us immediately at <a href="mailto:support@rentual.co">support@rentual.co</a> if you suspect unauthorized access to your account.</li>
          </ul>
        </div>

        <div className="dossier-section">
          <h3>4. Platform Services</h3>
          <p>Rentual provides a technology platform that enables:</p>
          <ul>
            <li>Property listing creation and management</li>
            <li>Tenant application submission and processing</li>
            <li>Identity verification through third-party providers</li>
            <li>Credit and background screening through consumer reporting agencies</li>
            <li>AI-assisted document review and risk assessment</li>
            <li>Rent collection and payment processing</li>
            <li>Lease document generation, e-signing, and management</li>
            <li>Communication between Landlords and Tenants</li>
            <li>Maintenance request tracking</li>
          </ul>
          <p>
            Rentual is a technology platform provider. We are not a real estate broker, property manager, landlord,
            tenant, or consumer reporting agency. We do not participate in rental decisions and do not guarantee the
            accuracy of any information provided by Users or third-party services.
          </p>
        </div>

        <div className="dossier-section">
          <h3>5. Tenant Screening Services</h3>
          <ul>
            <li>
              Screening reports are sourced from TransUnion and other third-party consumer reporting agencies.
              Rentual facilitates access to these reports but does not itself generate, create, or guarantee the
              accuracy of credit data or background information.
            </li>
            <li>
              Landlords must obtain the Tenant's explicit, informed consent before ordering any screening report.
              Consent is captured through the Rentual workflow and recorded for compliance purposes.
            </li>
            <li>
              Screening reports are provided for rental evaluation purposes only, constituting a permissible purpose
              under applicable Canadian law, including the Consumer Reporting Act (Ontario) and equivalent provincial
              legislation.
            </li>
            <li>
              Landlords must not use screening data for any purpose other than evaluating a specific tenancy application.
              Sharing screening data with unauthorized third parties is strictly prohibited.
            </li>
            <li>
              AI-assisted document review uses third-party AI services for field extraction, fraud detection, and
              risk assessment. AI outputs are advisory only and do not constitute recommendations or decisions.
              Landlords are solely responsible for all rental decisions.
            </li>
          </ul>
        </div>

        <div className="dossier-section">
          <h3>6. Identity Verification</h3>
          <p>
            The Platform uses Stripe Identity for identity verification, which may involve government-issued
            identification and selfie capture. Verification data is processed by Stripe in accordance with their
            privacy policy. Rentual does not store biometric data. By using identity verification features, you
            consent to Stripe's processing of your verification data.
          </p>
        </div>

        <div className="dossier-section">
          <h3>7. Fees and Payments</h3>
          <ul>
            <li>Certain Services require payment, including subscription fees, screening report fees, and transaction fees.</li>
            <li>All fees are displayed at the time of purchase and are in Canadian dollars unless otherwise stated.</li>
            <li>Payment processing is handled by Stripe. By using payment features, you agree to Stripe's terms of service.</li>
            <li>Subscription plans renew automatically unless cancelled before the renewal date. You may cancel at any time through your account settings.</li>
            <li>Refunds are handled on a case-by-case basis. See our <Link to="/refunds">Refund Policy</Link> for details.</li>
          </ul>
        </div>

        <div className="dossier-section">
          <h3>8. User Content</h3>
          <ul>
            <li>Users are solely responsible for all Content they upload, submit, or share through the Platform.</li>
            <li>You represent and warrant that you have the right to share any Content you submit and that it does not violate any law or third-party rights.</li>
            <li>Rentual may review, remove, or disable access to Content that violates these Terms or applicable law, without prior notice.</li>
            <li>By submitting Content, you grant Rentual a non-exclusive, worldwide license to use, process, store, and display that Content solely for the purpose of providing the Services.</li>
          </ul>
        </div>

        <div className="dossier-section">
          <h3>9. Prohibited Uses</h3>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Platform for any fraudulent, deceptive, or illegal purpose</li>
            <li>Harass, threaten, or discriminate against any User</li>
            <li>Submit false, misleading, or inaccurate information</li>
            <li>Attempt to reverse engineer, decompile, or disassemble any part of the Platform</li>
            <li>Use automated systems (bots, scrapers) to access the Platform without written permission</li>
            <li>Interfere with or disrupt the Platform's infrastructure or security measures</li>
            <li>Circumvent any access controls, rate limits, or usage restrictions</li>
            <li>Use screening data for purposes other than evaluating a specific tenancy application</li>
            <li>Share login credentials or allow unauthorized persons to access your account</li>
            <li>Upload malicious code, viruses, or other harmful content</li>
          </ul>
        </div>

        <div className="dossier-section">
          <h3>10. Limitation of Liability</h3>
          <p>
            <strong>THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
            WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO WARRANTIES OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</strong>
          </p>
          <ul>
            <li>
              Rentual is not responsible for the accuracy, completeness, or reliability of any third-party data,
              including credit reports, background check results, identity verification outcomes, or any other
              information obtained from external providers.
            </li>
            <li>
              Rentual is not liable for rental decisions made by Landlords, including decisions to approve, reject,
              or condition a tenancy application.
            </li>
            <li>
              Rentual is not liable for any losses, damages, or claims arising from screening outcomes, including
              but not limited to loss of housing, loss of rental income, or reputational harm.
            </li>
            <li>
              Landlords are solely responsible for complying with all applicable human rights codes, fair housing
              laws, residential tenancy legislation, and anti-discrimination laws in their jurisdiction.
            </li>
            <li>
              AI-generated analysis, risk scores, and document review outputs are advisory only and do not
              constitute professional advice. Rentual makes no representations regarding the accuracy or
              completeness of AI-generated outputs.
            </li>
          </ul>
          <p>
            <strong>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, RENTUAL'S TOTAL LIABILITY TO YOU FOR ALL
              CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR YOUR USE OF THE PLATFORM SHALL NOT EXCEED
              THE TOTAL FEES PAID BY YOU TO RENTUAL IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </strong>
          </p>
          <p>
            IN NO EVENT SHALL RENTUAL BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
            PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, BUSINESS OPPORTUNITIES, OR GOODWILL.
          </p>
        </div>

        <div className="dossier-section">
          <h3>11. Indemnification</h3>
          <p>
            You agree to indemnify, defend, and hold harmless Rentual Corp, its officers, directors, employees,
            and agents from and against any claims, damages, losses, liabilities, costs, and expenses (including
            reasonable legal fees) arising out of or related to:
          </p>
          <ul>
            <li>Your use of the Platform or violation of these Terms</li>
            <li>Your violation of any applicable law or third-party rights</li>
            <li>Rental decisions you make based on information obtained through the Platform</li>
            <li>Content you submit or share through the Platform</li>
            <li>Your failure to obtain required consents from Tenants or other individuals</li>
          </ul>
        </div>

        <div className="dossier-section">
          <h3>12. Privacy</h3>
          <p>
            Your use of the Platform is also governed by our <Link to="/privacy">Privacy Policy</Link>, which
            describes how we collect, use, store, and share your personal information. By using the Platform,
            you consent to the practices described in the Privacy Policy.
          </p>
        </div>

        <div className="dossier-section">
          <h3>13. Third-Party Services</h3>
          <p>
            The Platform integrates with third-party services, including but not limited to:
          </p>
          <ul>
            <li><strong>Stripe</strong> - payment processing, subscription billing, and identity verification</li>
            <li><strong>TransUnion</strong> - credit reports and consumer screening data</li>
            <li><strong>Anthropic (Claude)</strong> - AI-assisted document analysis and risk assessment</li>
            <li><strong>Cloudinary</strong> - document and image storage and processing</li>
            <li><strong>Postmark</strong> - transactional email delivery</li>
          </ul>
          <p>
            Your use of these third-party services is subject to their respective terms and privacy policies.
            Rentual is not responsible for the availability, accuracy, or security practices of third-party
            services.
          </p>
        </div>

        <div className="dossier-section">
          <h3>14. Termination</h3>
          <ul>
            <li>You may close your account at any time by contacting <a href="mailto:support@rentual.co">support@rentual.co</a>.</li>
            <li>We may suspend or terminate your account at any time for violation of these Terms, security concerns, legal requirements, or at our discretion with reasonable notice.</li>
            <li>Upon termination, your right to access the Platform ceases immediately. Data retention following termination is governed by our <Link to="/privacy">Privacy Policy</Link>.</li>
            <li>Sections 10, 11, 12, and 15 survive termination of these Terms.</li>
          </ul>
        </div>

        <div className="dossier-section">
          <h3>15. Governing Law</h3>
          <p>
            These Terms are governed by and construed in accordance with the laws of the Province of Ontario,
            Canada, without regard to conflict of law principles. Any disputes arising under these Terms shall
            be resolved in the courts of the Province of Ontario, and you consent to the exclusive jurisdiction
            of such courts.
          </p>
        </div>

        <div className="dossier-section">
          <h3>16. Changes to Terms</h3>
          <p>
            Rentual may update these Terms from time to time. We will notify you of material changes by email
            or through a notice on the Platform. Your continued use of the Platform after changes take effect
            constitutes acceptance of the updated Terms. If you do not agree with the changes, you must stop
            using the Platform and close your account.
          </p>
        </div>

        <div className="dossier-section">
          <h3>17. Contact</h3>
          <p>
            If you have questions about these Terms, please contact us at{' '}
            <a href="mailto:support@rentual.co">support@rentual.co</a>.
          </p>
        </div>
      </div>
    </main>
  );
}

export default Terms;
